import React from "react";
import { motion } from "framer-motion";
import { DatabaseIcon, LineChartIcon, Link2Icon, NetworkIcon, ShieldCheckIcon, TargetIcon, UsersIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { atnaChallengeBarriers, atnaChallengeCopy as c, Bi, ChallengeBarrier } from "../../data/atnaChallenge";
type Pick = (b: Bi) => string;
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const ICONS: Record<ChallengeBarrier['key'], {
  main: BoxIcon;
  insight: BoxIcon;
}> = {
  process: {
    main: NetworkIcon,
    insight: LineChartIcon
  },
  systems: {
    main: DatabaseIcon,
    insight: Link2Icon
  },
  trust: {
    main: ShieldCheckIcon,
    insight: UsersIcon
  }
};
function Barrier({
  b,
  index,
  pick
}: {
  b: ChallengeBarrier;
  index: number;
  pick: Pick;
}) {
  const {
    main: Icon,
    insight: InsightIcon
  } = ICONS[b.key];
  const split = b.stats.length > 1;
  const layout = ['pb-8 md:pb-0 md:pr-7 xl:pl-8 xl:border-l', 'py-8 md:py-0 border-t md:border-t-0 md:border-l md:pl-7 xl:px-8', 'pt-8 border-t md:col-span-2 md:mt-8 xl:mt-0 xl:col-span-1 xl:pt-0 xl:border-t-0 xl:border-l xl:pl-8'][index];
  return <article className={`min-w-0 flex flex-col border-[#E6E5F3] ${layout}`} data-id="element-756" data-mp="282ktujxhfh3s">
      <span className="w-14 h-14 rounded-full bg-[#F1F0FD] flex items-center justify-center" data-id="element-757" data-mp="284ovswbtm61f">
        <Icon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.5} aria-hidden="true" data-id="element-758" data-mp="2433p5spvqnzo" />
      </span>
      <h3 className="mt-6 font-sans text-[12.5px] font-semibold tracking-[0.14em] uppercase text-[#121A3A]" data-id="element-759" data-mp="1z867jxejb6b6">
        <span className="text-[#6B7390] font-medium mr-1.5" data-id="element-760" data-mp="1u9d30uh86pn5">{b.number}</span>
        {pick(b.title)}
      </h3>

      <div className={`mt-4 ${split ? 'grid grid-cols-2 gap-5' : ''}`} data-id="element-761" data-mp="2mzv6hwlv2t13">
        {b.stats.map((s, i) => <div key={s.value.en} className={split && i > 0 ? 'pl-5 border-l border-[#E6E5F3]' : ''} data-id="element-762" data-mp="35w3omde7ocye" data-mpi={i}>
            <p className="font-serif text-[2.2rem] lg:text-[2.4rem] leading-none text-[#4F46E5] whitespace-nowrap" data-id="element-763" data-mp="3av6ug020mn7f">{pick(s.value)}</p>
            <p className="mt-3 font-sans text-[14.5px] leading-snug text-[#2A3150] max-w-[320px]" data-id="element-764" data-mp="2x2qpgtpk0f3v">{pick(s.label)}</p>
          </div>)}
      </div>

      <p className="mt-4 font-sans text-[12.5px] text-[#7A8098] whitespace-nowrap" data-id="element-765" data-mp="313aknrqkh8nd">{pick(b.source)}</p>

      <div className="mt-auto pt-5" data-id="element-766" data-mp="2n7yrhos27q8k">
        <div className="flex items-center gap-4 rounded-2xl bg-[#F7F7FD] px-4 py-3.5" data-id="element-767" data-mp="2s4yyc1wqgx0q">
          <span className="w-11 h-11 rounded-full bg-[#EEEDFC] flex items-center justify-center shrink-0" data-id="element-768" data-mp="2e57fnnv9usge">
            <InsightIcon className="w-5 h-5 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-769" data-mp="2i5zq2kfvr3e9" />
          </span>
          <p className="font-sans text-[13.5px] leading-snug text-[#2A3150]" data-id="element-770" data-mp="cem7we2juqcq">{pick(b.insight)}</p>
        </div>
      </div>
    </article>;
}
export function AtnaChallenge() {
  const {
    lang
  } = useLanguage();
  const pick: Pick = b => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-[#FBFBFE] py-20 lg:py-24 px-6" aria-labelledby="atna-challenge-title" data-id="element-771" data-mp="1wvvd9ufn9ft0">
      <div className="relative max-w-[1440px] mx-auto" data-id="element-772" data-mp="2ao1s9d729gqv">
        <div className="grid grid-cols-1 xl:grid-cols-[34fr_66fr] gap-12 xl:gap-10 items-stretch" data-id="element-773" data-mp="25wa04y89b2ym">
          <motion.div {...reveal} className="min-w-0" data-id="element-774" data-mp="2kl652mf0nzv0">
            <p className="font-sans text-[13px] md:text-[14px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-775" data-mp="2kjv56xoozc96">{pick(c.eyebrow)}</p>
            <h2 id="atna-challenge-title" className="font-serif text-[2.2rem] sm:text-[2.7rem] lg:text-[3rem] leading-[1.04] tracking-tight mt-5" data-id="element-776" data-mp="2foftoce5psfx">
              {c.headline.map(l => <span key={l.text.fr} className={`block sm:whitespace-nowrap ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-777" data-mp="2onazvoh7b2j8">
                  {pick(l.text)}
                </span>)}
            </h2>
            <p className="mt-7 max-w-[520px] font-sans text-[15.5px] md:text-[16px] text-[#3A4260] leading-relaxed" data-id="element-778" data-mp="ual22vnpp96k">{pick(c.body)}</p>
          </motion.div>

          <motion.div {...reveal} className="min-w-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3" data-id="element-779" data-mp="10oh3tgts0zjm">
            {atnaChallengeBarriers.map((b, i) => <Barrier key={b.key} b={b} index={i} pick={pick} data-id="element-780" data-mp="15owiqctpif3p" data-mpi={i} />)}
          </motion.div>
        </div>

        <motion.div {...reveal} className="relative mt-12 lg:mt-14 overflow-hidden rounded-3xl border border-[#ECEBF7] bg-white px-6 md:px-8 py-6 md:py-7 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8" data-id="element-781" data-mp="1ametm8rab4ec">
          <div className="pointer-events-none absolute -right-24 -top-24 w-[420px] h-[320px] rounded-full bg-[#EEEDFC] opacity-70 blur-3xl" aria-hidden="true" data-id="element-782" data-mp="hwg286pmav6i" />
          <span className="relative w-16 h-16 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" data-id="element-783" data-mp="rpdjyamscg42">
            <TargetIcon className="w-7 h-7 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-784" data-mp="3qdswa4e803ln" />
          </span>
          <span className="relative hidden sm:block self-stretch w-px bg-[#E6E5F3]" aria-hidden="true" data-id="element-785" data-mp="3vbcc38qkgofp" />
          <p className="relative font-serif text-[1.35rem] md:text-[1.6rem] lg:text-[1.75rem] leading-[1.2] tracking-tight" data-id="element-786" data-mp="3nrtjcaritgtx">
            <span className="block text-[#121A3A]" data-id="element-787" data-mp="3dvgjsw9ao8r9">{pick(c.conclusion.lead)}</span>
            {c.conclusion.accent.map(l => <span key={l.fr} className="block text-[#4F46E5]" data-id="element-788" data-mp="38x5bipyne6b5">{pick(l)}</span>)}
          </p>
        </motion.div>
      </div>
    </section>;
}